import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Columns2, Frame, LayoutTemplate, RotateCcw, Rows2, Sparkles } from 'lucide-react';
import { InputField } from '../shared/InputGroup.jsx';
import { Button } from '../shared/Button.jsx';
import { jsonRenderCatalog, detailsFromDescribed } from '../shared/json-render.js';
import { previewOf } from './preview-api.js';

// The frame never grows past this (a long scrolling list would otherwise stretch it).
const MAX_FRAME = 2400;

/**
 * Compose (config `ask`, dev only; `?path=/compose`): describe a screen and
 * Jev builds it from the library's own stories (src/node/compose.js).
 *
 * One preview frame (`preview.html?compose`) does both halves in the browser:
 * it describes every story file once, which becomes the json-render catalog
 * sent with each prompt, and it renders the answer with the project's own
 * renderer. Once a screen exists, the next prompt edits it.
 */
export function Compose({ index, config, globals, onNavigate }) {
  const frameRef = useRef(null);
  const catalogRef = useRef(null);
  const [prep, setPrep] = useState({ state: 'loading', done: 0, total: 0 });
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { spec, placed, stopReason, ms, tokens, prompt }
  const [error, setError] = useState(null);
  const resultRef = useRef(null);
  resultRef.current = result;

  // Describe every story file in the frame once it has the index.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await previewOf(frameRef.current);
        const files = [...new Set(Object.values(index.entries).filter((e) => e.type === 'story').map((e) => e.importPath))];
        setPrep({ state: 'loading', done: 0, total: files.length });
        const described = [];
        for (const file of files) {
          if (cancelled) return;
          try {
            described.push(await (await previewOf(frameRef.current)).describe(file));
          } catch {}
          setPrep((p) => ({ ...p, done: p.done + 1 }));
        }
        const catalog = jsonRenderCatalog(detailsFromDescribed(described), { title: config.title, renderer: config.renderer });
        catalogRef.current = { components: catalog.components, candidates: catalog.candidates };
        if (!cancelled) setPrep({ state: 'ready', candidates: catalog.candidates.length, components: Object.keys(catalog.components).length });
      } catch (e) {
        if (!cancelled) setPrep({ state: 'error', message: e.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [index]);

  // Draw the screen again when the theme changes, or when Vite reloads the frame.
  useEffect(() => {
    if (resultRef.current?.spec) draw(resultRef.current);
  }, [JSON.stringify(globals)]);
  const onFrameLoad = () => {
    setFrameLoads((n) => n + 1);
    if (resultRef.current?.spec) draw(resultRef.current);
  };

  // The frame grows to fit what the screen draws, also later (a dialog opened
  // by a click). A dialog sizes itself to the frame, so in a short frame its
  // form ran past its panel. It only grows, so a centred dialog cannot make
  // it bounce; a new screen starts from the page's height again.
  const [frameHeight, setFrameHeight] = useState(null);
  const [frameLoads, setFrameLoads] = useState(0);
  useEffect(() => setFrameHeight(null), [result?.spec]);
  useEffect(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!doc?.body) return;
    let timer = null;
    // What the screen needs: the page's own height, and the frame's height
    // plus the largest spill out of a box that does not scroll. Axon's dialog
    // caps its panel at the frame's height minus 256px and its form spilled
    // out of it; a taller frame raises that cap by the same amount.
    const measure = () => {
      const win = doc.defaultView;
      if (!win) return; // the frame is reloading
      let spill = 0;
      for (const el of doc.body.querySelectorAll('*')) {
        const extra = el.scrollHeight - el.clientHeight;
        if (extra > 1 && el.clientHeight > 0 && win.getComputedStyle(el).overflowY === 'visible') spill = Math.max(spill, extra);
      }
      const needed = Math.min(Math.ceil(Math.max(doc.documentElement.scrollHeight, win.innerHeight + spill + (spill ? 24 : 0))), MAX_FRAME);
      if (needed > frame.clientHeight + 1) setFrameHeight((h) => Math.max(h ?? 0, needed));
    };
    // After animations settle (dialogs scale in).
    const later = () => {
      clearTimeout(timer);
      timer = setTimeout(measure, 350);
    };
    const mo = new MutationObserver(later);
    mo.observe(doc.body, { childList: true, subtree: true, attributes: true });
    later();
    return () => {
      mo.disconnect();
      clearTimeout(timer);
    };
  }, [frameLoads, result]);

  async function draw(r) {
    const preview = await previewOf(frameRef.current);
    const out = await preview.compose({ spec: r.spec, placed: r.placed, texts: r.texts ?? {} }, globals);
    if (out && !out.ok && !out.stale) setError(`The screen did not render: ${out.error}`);
  }

  // With a screen on show, a prompt that opens with an edit word changes it;
  // anything else builds a new screen (a full description typed after a first
  // answer once only swapped that answer's one piece).
  const hasScreen = Boolean(result?.spec);
  const editing = hasScreen && EDIT.test(prompt.trim());

  async function submit() {
    const p = prompt.trim();
    if (!p || busy || prep.state !== 'ready') return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/__edl/compose', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: p, catalog: catalogRef.current, spec: editing ? result.spec : null, texts: editing ? result.texts ?? {} : {} }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      // A change that finds nothing keeps the screen as it was; a new screen that finds nothing clears it.
      const next = data.emptied
        ? { ...data, edited: true }
        : data.spec
        ? { ...data, edited: editing }
        : editing
          ? { ...result, stopReason: data.stopReason, ms: data.ms, tokens: data.tokens, nothing: true }
          : { stopReason: data.stopReason, ms: data.ms, tokens: data.tokens, nothing: true };
      // The prompt stays in the field, so it can be refined and sent again.
      setResult({ ...next, sent: p });
      await draw(next.spec ? next : { spec: null, placed: {} });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function startOver() {
    setResult(null);
    setError(null);
    previewOf(frameRef.current).then((p) => p.compose({ spec: null, placed: {} }, globals));
  }

  const note = noteFor(result);
  const status =
    prep.state === 'loading'
      ? prep.total
        ? `Reading the library · ${prep.done} of ${prep.total}`
        : 'Reading the library…'
      : busy
        ? 'Composing…'
        : prep.state === 'ready'
          ? `${prep.components} ${prep.components === 1 ? 'component' : 'components'}`
          : null;

  return (
    <div className="shell-compose">
      {/* The story page's frame: toolbar row, display title with a status line, then the work. */}
      <div className="shell-compose__toolbar">
        <span className="shell-compose__eyebrow">Compose</span>
        {hasScreen && (
          <Button variant="ghost" size="compact" leadingIcon={RotateCcw} onClick={startOver}>
            Start over
          </Button>
        )}
      </div>
      <header className="shell-compose__head">
        <h1 className="shell-compose__title">Build a screen from the library</h1>
        {status && (
          <p className={`shell-compose__status${busy || prep.state === 'loading' ? ' is-working' : ''}`} aria-live="polite">
            {status}
          </p>
        )}
      </header>

      <div className="shell-compose__ask">
        <InputField
          label="Describe a screen"
          labelHidden
          icon={Sparkles}
          value={prompt}
          onChange={setPrompt}
          disabled={prep.state !== 'ready'}
          placeholder={prep.state === 'loading' ? 'Reading the library…' : hasScreen ? 'A new screen, or start with “add”, “remove”, “make”… to change this one' : 'e.g. a modal with a registration form'}
          className={busy ? 'is-busy' : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
      </div>

      {(note || error || prep.state === 'error') && (
        <p className="shell-compose__note" aria-live="polite">
          {prep.state === 'error' ? `The preview did not load: ${prep.message}` : error ?? note}
        </p>
      )}

      <div className="shell-compose__body">
        <div className={`shell-compose__frame${result?.spec ? ' has-screen' : ''}`} style={frameHeight ? { height: frameHeight } : undefined}>
          <iframe ref={frameRef} className="shell-compose__iframe" title="Composed screen" src="/preview.html?compose" onLoad={onFrameLoad} />
          {!result?.spec && (
            <div className={`shell-compose__empty${busy ? ' is-working' : ''}`}>
              <LayoutTemplate size={20} strokeWidth={1.5} aria-hidden="true" />
              <span>{busy ? 'Composing…' : 'The screen appears here.'}</span>
            </div>
          )}
        </div>
        {result?.spec && (
          <aside className="shell-compose__parts" aria-label="On this screen">
            <p className="shell-compose__parts-label">On this screen</p>
            <ol className="shell-compose__tree">
              <Part id={result.spec.root} spec={result.spec} placed={result.placed} texts={result.texts ?? {}} index={index} onNavigate={onNavigate} />
            </ol>
          </aside>
        )}
      </div>
    </div>
  );
}

function Part({ id, spec, placed, texts, index, onNavigate }) {
  const el = spec.elements[id];
  if (!el) return null;
  const storyId = placed[id];
  const entry = storyId ? index.entries[storyId] : null;
  const kids = el.children ?? [];
  return (
    <li className="shell-compose__part">
      {entry ? (
        <button type="button" className="shell-compose__link" onClick={() => onNavigate(storyId)}>
          <Frame size={14} strokeWidth={1.5} aria-hidden="true" />
          <span className="shell-compose__link-text">
            <span className="shell-compose__name">
              {entry.title.split('/').at(-1)} <span className="shell-compose__story">{entry.name}</span>
            </span>
            {labelOf(texts[id]) && <span className="shell-compose__text">“{labelOf(texts[id])}”</span>}
          </span>
          <ArrowUpRight className="shell-compose__go" size={14} strokeWidth={1.5} aria-hidden="true" />
        </button>
      ) : (
        <span className="shell-compose__layout">
          {el.props?.direction === 'horizontal' ? <Columns2 size={14} strokeWidth={1.5} aria-hidden="true" /> : <Rows2 size={14} strokeWidth={1.5} aria-hidden="true" />}
          {el.props?.direction === 'horizontal' ? 'Row' : 'Stack'}
        </span>
      )}
      {kids.length > 0 && (
        <ol className="shell-compose__tree">
          {kids.map((k) => (
            <Part key={k} id={k} spec={spec} placed={placed} texts={texts} index={index} onNavigate={onNavigate} />
          ))}
        </ol>
      )}
    </li>
  );
}

const EDIT = /^(add|also|remove|delete|drop|take|make|change|replace|swap|move|put|turn|use|show|hide|set|switch|without|instead)\b/i;

const labelOf = (t) => t?.children ?? t?.label ?? t?.['aria-label'] ?? null;

function noteFor(result) {
  if (!result) return null;
  if (result.nothing) return result.spec ? 'Nothing in the library fits that change; the screen is as it was.' : 'Nothing in the library fits that.';
  if (result.emptied) return 'Removed. The screen is empty.';
  if (result.missing?.length) return `No story in the library for: ${result.missing.join('; ')}. This is what it has.`;
  if (result.stopReason === 'unavailable') return 'Part of that has no story in the library. This is what it has.';
  if (result.stopReason === 'limit') return 'Stopped at the step limit.';
  return null;
}
